import { Badge, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nexus/ui";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { CUSTOM_PRESET_ID } from "@/features/dashboard/lib/explore-date-presets";
import { PERSONA_EXTRA_ROLLING_PRESET } from "@/features/opportunities/components/sets/CreateSetDateRangePicker/CreateSetDateRangePicker.js";
import { describeSetDateWindow } from "@/features/opportunities/lib/set-date-window-display";

import type { PersonaOpportunitySet } from "../../../types/persona-opportunities.types.js";
import { hubSetTypeLabel } from "./hub-set-type-label.js";

type Translate = ReturnType<typeof useTranslation>["t"];

const TOOLTIP_DELAY_MS = 300;

/**
 * Names the set a hub group belongs to. Hovering lists the set's parameters
 * when the set is loaded; otherwise only the name is known.
 */
export function OpportunityHubSetBadge({
  name,
  set,
}: {
  name: string;
  set?: PersonaOpportunitySet | null;
}) {
  const { t } = useTranslation();
  const badge = (
    <Badge
      variant="slate"
      iconStart="folder"
      className="max-w-48 text-[11px]"
      title={set ? undefined : name}
      data-testid="opportunity-hub-set-badge"
    >
      <span className="sr-only">{t("personaOpportunities.hub.scripts.columns.set", "Set")}: </span>
      <span className="truncate">{name}</span>
    </Badge>
  );
  if (!set) return badge;

  return (
    <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent align="start" className="px-3 py-2">
          <OpportunityHubSetParams set={set} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** A set's name, type, status, date window and counts, for hover cards. */
export function OpportunityHubSetParams({ set }: { set: PersonaOpportunitySet }) {
  const { t } = useTranslation();
  const dateWindow = describeSetDateWindow(set.startDate, set.endDate, t, [PERSONA_EXTRA_ROLLING_PRESET]);
  const isCustom = set.type === "USER_GENERATED";

  return (
    <div className="flex flex-col gap-1.5" data-testid="opportunity-hub-set-params">
      <p className="truncate font-medium">{set.name}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
        <SetParam label={t("personaOpportunities.hub.sets.columns.type", "Type")}>
          {hubSetTypeLabel(t, set.type)}
        </SetParam>
        <SetParam label={t("personaOpportunities.hub.sets.columns.status", "Status")}>
          {setStatusLabel(t, set.status)}
        </SetParam>
        {dateWindow ? (
          <SetParam label={t("personaOpportunities.hub.sets.columns.window", "Date window")}>
            {dateWindow.presetId === CUSTOM_PRESET_ID
              ? dateWindow.rangeLabel
              : `${dateWindow.presetLabel} · ${dateWindow.rangeLabel}`}
          </SetParam>
        ) : null}
        {isCustom ? null : (
          <SetParam
            label={t("personaOpportunities.hub.sets.columns.opportunities", "Opportunities")}
          >
            {set.opportunityCount}
          </SetParam>
        )}
        <SetParam label={t("personaOpportunities.hub.sets.columns.scripts", "Scripts")}>
          {set.scriptCount}
        </SetParam>
        {set.sentCount != null ? (
          <SetParam label={t("personaOpportunities.hub.sets.columns.sent", "Sent")}>
            {set.sentCount}
          </SetParam>
        ) : null}
      </dl>
    </div>
  );
}

function SetParam({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="opacity-70">{label}</dt>
      <dd className="tabular-nums">{children}</dd>
    </>
  );
}

function setStatusLabel(t: Translate, status: PersonaOpportunitySet["status"]): string {
  if (status === "READY") return t("personaOpportunities.hub.sets.statusReady", "Ready");
  if (status === "GENERATING") {
    return t("personaOpportunities.hub.sets.statusGenerating", "Generating");
  }
  return t("personaOpportunities.hub.sets.statusFailed", "Failed");
}
